import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useStatusStore from "../../store/statusStore";
import { getTodayStatus } from "../../config/hours";
import { SITE_NAME } from "../../config/location";
import useMediaQuery, { REDUCED_MOTION } from "../site/useMediaQuery";

// Startseiten-Hero: Grill-Video (Standbild als Poster/Fallback), Claim, Heute-Status.
export default function Hero() {
    const reducedMotion = useMediaQuery(REDUCED_MOTION);
    const [videoReady, setVideoReady] = useState(false);
    const photoRef = useRef(null);
    const { closure, specialOpening, fetchSpecialOpening } = useStatusStore();
    const today = getTodayStatus(new Date(), { closure, specialOpening });

    useEffect(() => {
        fetchSpecialOpening();
    }, [fetchSpecialOpening]);

    // Leichte Parallaxe auf dem Standbild.
    useEffect(() => {
        if (reducedMotion) return undefined;
        const onScroll = () => {
            const y = Math.min(window.scrollY, window.innerHeight);
            if (photoRef.current) {
                photoRef.current.style.transform = `translateY(${y * 0.25}px)`;
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [reducedMotion]);

    return (
        // Mobil wächst der Hero mit dem Text (mind. 88svh), damit nichts unter den Header rutscht.
        <section className="relative flex min-h-[88svh] flex-col justify-end desk:block desk:min-h-0 desk:flex-1 overflow-hidden bg-nacht">
            <img
                ref={photoRef}
                src="/media/grill.jpg"
                alt="Lammkoteletts auf dem Grill"
                className="absolute left-0 top-[-12%] h-[124%] w-full object-cover object-[70%_50%] will-change-transform"
            />
            {!reducedMotion && (
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster="/media/grill.jpg"
                    aria-hidden="true"
                    onPlaying={() => setVideoReady(true)}
                    className="absolute inset-0 z-[1] h-full w-full object-cover object-[60%_50%] transition-opacity duration-700"
                    style={{ opacity: videoReady ? 1 : 0 }}
                >
                    <source src="/media/hero.mp4" type="video/mp4" />
                </video>
            )}
            <div className="absolute inset-0 z-[2] bg-[linear-gradient(90deg,rgba(27,47,36,.9)_0%,rgba(27,47,36,.72)_42%,rgba(27,47,36,.3)_70%,rgba(27,47,36,.05)_100%)]" />
            <div className="absolute inset-0 z-[2] bg-[linear-gradient(0deg,rgba(27,47,36,.88)_0%,rgba(27,47,36,.6)_35%,rgba(27,47,36,0)_65%)]" />

            <div className="relative z-[3] px-6 pb-10 pt-[calc(var(--header-h,65px)+40px)] desk:absolute desk:p-0 desk:left-[var(--gutter)] desk:right-[var(--gutter)] desk:bottom-[72px] xwide:bottom-24 flex flex-col items-start gap-6 desk:flex-row desk:items-end desk:justify-between desk:gap-12 text-creme">
                <div className="max-w-[760px] xwide:max-w-[1240px]">
                    <div className="mb-7 flex items-center gap-3.5 hero-in" style={{ animationDelay: ".2s" }}>
                        <span className="line-grow block h-px w-10 bg-messing-hell" />
                        <span className="text-xs font-semibold uppercase tracking-[.26em] text-messing-hell">
                            {SITE_NAME} · Weingarten (Baden)
                        </span>
                    </div>
                    <h1 className="h1-page mb-7 hero-in" style={{ animationDelay: ".4s" }}>
                        Griechische Küche.
                        <br />
                        Frisch vom Grill.
                    </h1>
                    <p
                        className="mb-9 max-w-[480px] xwide:max-w-[560px] text-lg xwide:text-xl leading-[1.6] text-creme/85 hero-in"
                        style={{ animationDelay: ".6s" }}
                    >
                        Lamm, Rind, Fisch und Meze. Mittags unsere Mittagskarte, abends die ganze Karte.
                    </p>
                    <div className="flex flex-wrap items-center gap-7 hero-in" style={{ animationDelay: ".75s" }}>
                        <Link to="/reservierung" className="btn btn-light">
                            Tisch reservieren
                        </Link>
                        <Link to="/karte" className="link-underline !border-creme/60">
                            Speisekarte
                        </Link>
                    </div>
                </div>
                <div
                    className="text-left desk:text-right text-sm xwide:text-[15px] leading-[1.7] text-creme/75 hero-in"
                    style={{ animationDelay: ".9s" }}
                >
                    <div className="font-caslon text-lg xwide:text-[22px] italic text-creme">{today.label}</div>
                    {today.hours}
                </div>
            </div>
        </section>
    );
}
